class QuickAddDrawer extends HTMLElement {
  constructor() {
    super();
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleSizeClick = this.handleSizeClick.bind(this);
    this.handleSizeGuideClick = this.handleSizeGuideClick.bind(this);
    this.currentProductData = null;
    this.selectedVariantId = null;
  }

  // Debug marker (permet de vérifier que ce fichier est bien chargé)
  static debugLoaded() {
    try {
      if (window?.localStorage?.getItem('debugQuickAdd') === '1') {
        // eslint-disable-next-line no-console
        console.log('[QuickAddDrawer] quick-add-drawer.js chargé');
      }
    } catch (e) {
      // ignore
    }
  }

  /**
   * Debug helper (activable via localStorage.setItem('debugQuickAdd','1'))
   */
  debug(...args) {
    try {
      if (window?.localStorage?.getItem('debugQuickAdd') === '1') {
        // eslint-disable-next-line no-console
        console.log('[QuickAddDrawer]', ...args);
      }
    } catch (e) {
      // ignore
    }
  }

  connectedCallback() {
    QuickAddDrawer.debugLoaded();
    this.overlay = this.querySelector(".quick-add-drawer-overlay");
    this.container = this.querySelector(".quick-add-drawer-container");
    this.closeButton = this.querySelector(".quick-add-drawer-close");
    this.submitButton = this.querySelector(".quick-add-drawer-submit");
    this.form = this.querySelector("#QuickAddDrawerForm");
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.titleElement = this.querySelector(".quick-add-drawer-title");
    this.colorElement = this.querySelector(".quick-add-drawer-color");
    this.priceElement = this.querySelector(".quick-add-drawer-price");
    this.sizesContainer = this.querySelector(".quick-add-drawer-sizes");
    this.sizeGuideContainer = this.querySelector(".quick-add-drawer-size-guide");

    if (this.overlay) {
      this.overlay.addEventListener("click", this.handleOverlayClick);
    }
    if (this.closeButton) {
      this.closeButton.addEventListener("click", this.handleClose);
    }
    if (this.submitButton) {
      this.submitButton.addEventListener("click", this.handleSubmit);
    }

    document.addEventListener("keydown", this.handleEscape);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleEscape);
  }

  /**
   * Ouvre le drawer et le remplit avec les données du produit
   * @param {Object} productData - Données du produit
   */
  async open(productData) {
    if (!productData) {
      return;
    }

    this.debug('open()', {
      productId: productData.productId,
      productHandle: productData.productHandle,
      selectedVariantId: productData.selectedVariantId,
      price: productData.price,
      compareAtPrice: productData.compareAtPrice,
      priceType: typeof productData.price,
      compareAtPriceType: typeof productData.compareAtPrice,
      variantsLen: Array.isArray(productData.variants) ? productData.variants.length : null,
    });

    this.currentProductData = productData;
    this.classList.add("is-open");
    document.body.classList.add("quick-add-drawer-open");
    await this.populate(productData);
  }

  /**
   * Ferme le drawer
   */
  close() {
    this.classList.remove("is-open");
    document.body.classList.remove("quick-add-drawer-open");
    this.selectedVariantId = null;
    this.currentProductData = null;
  }

  /**
   * Met à jour uniquement la couleur et les variants (appelé lors d'un changement de couleur)
   * @param {Object} productData - Données du produit mises à jour
   */
  async updateColorAndVariants(productData) {
    if (!productData) return;

    // Mettre à jour les données courantes
    this.currentProductData = { ...this.currentProductData, ...productData };

    // Mettre à jour la couleur (même logique que populate)
    if (this.colorElement) {
      this.colorElement.innerHTML = "";

      let colorText = productData.color || null;

      // Si pas de couleur dans productData, chercher dans le DOM
      if (!colorText) {
        const productCards = document.querySelectorAll('.product-card, component-product-card, .collection__product-card');
        let switchColorSwatch = null;

        for (const productCard of productCards) {
          switchColorSwatch = productCard.querySelector('.product-switch-color .color-swatch.selected, switch-color-component .color-swatch.selected');
          if (switchColorSwatch) break;
        }

        if (!switchColorSwatch) {
          const switchColorComponents = document.querySelectorAll('switch-color-component');
          for (const component of switchColorComponents) {
            switchColorSwatch = component.querySelector('.color-swatch.selected');
            if (switchColorSwatch) break;
          }
        }

        if (switchColorSwatch) {
          colorText = switchColorSwatch.getAttribute('data-color-name') ||
                      switchColorSwatch.getAttribute('title')?.split(' - ')[0]?.trim() ||
                      switchColorSwatch.getAttribute('aria-label')?.split(' - ')[0]?.trim();
        }
      }

      // Afficher la couleur
      if (colorText) {
        const colorSpan = document.createElement("span");
        colorSpan.className = "quick-add-drawer-color-text";
        colorSpan.textContent = colorText;
        this.colorElement.appendChild(colorSpan);
      }
    }

    // Mettre à jour le prix si disponible
    // NB: certaines sources peuvent fournir price/compareAtPrice en string ("3500")
    const updatePrice = Number(productData.price);
    const updateCompareAt = Number(productData.compareAtPrice);
    if (this.priceElement && Number.isFinite(updatePrice) && updatePrice > 0) {
      this.priceElement.innerHTML = "";
      const priceContainer = document.createElement("div");
      priceContainer.className = "price__container";

      const priceDiv = (Number.isFinite(updateCompareAt) && updateCompareAt > updatePrice) ? document.createElement("div") : document.createElement("div");

      priceDiv.className = (Number.isFinite(updateCompareAt) && updateCompareAt > updatePrice) ? "price__sale" : "price__regular";

      const priceSpan = document.createElement("span");
      priceSpan.className = "jc-text-sm";
      priceSpan.textContent = this.formatMoney(updatePrice);
      priceDiv.appendChild(priceSpan);

      if (Number.isFinite(updateCompareAt) && updateCompareAt > updatePrice) {
        const compareSpan = document.createElement("s");
        compareSpan.className = "!jc-mr-[.8rem] jc-text-sm";
        compareSpan.textContent = this.formatMoney(updateCompareAt);
        priceDiv.appendChild(compareSpan);
      }

      priceContainer.appendChild(priceDiv);
      this.priceElement.appendChild(priceContainer);
    }

    // Mettre à jour les tailles
    if (this.sizesContainer && productData.variants && productData.variants.length > 0) {
      this.sizesContainer.innerHTML = "";

      const sizeOptionIndex = productData.sizeOptionIndex >= 0 ? productData.sizeOptionIndex : productData.variants[0]?.options?.length > 0 ? productData.variants[0].options.length - 1 : -1;

      const variantsBySize = this._deduplicateVariantsBySize(productData.variants, sizeOptionIndex, productData.selectedVariantId);
      variantsBySize.forEach((variant) => {
        const sizeValue = variant.size || (variant.options && sizeOptionIndex >= 0 ? variant.options[sizeOptionIndex] : "") || "";
        if (!sizeValue) return;

        const button = document.createElement("button");
        button.type = "button";
        button.className = "quick-add-drawer-size-button";
        button.textContent = sizeValue;
        button.dataset.variantId = variant.id;
        button.dataset.sizeValue = sizeValue;

        if (variant.available === false) {
          button.disabled = true;
        }

        if (variant.id.toString() === productData.selectedVariantId?.toString()) {
          button.classList.add("selected");
          this.selectedVariantId = variant.id;
          if (this.variantIdInput) {
            this.variantIdInput.value = variant.id;
          }
        }

        button.addEventListener("click", this.handleSizeClick);
        this.sizesContainer.appendChild(button);
      });
    }
  }

  /**
   * Une seule entrée par valeur de taille (priorité : variant sélectionné, puis premier disponible)
   * @param {Array} variants
   * @param {number} sizeOptionIndex
   * @param {string|number} selectedVariantId
   * @returns {Array}
   */
  _deduplicateVariantsBySize(variants, sizeOptionIndex, selectedVariantId) {
    if (!variants || variants.length === 0) return [];
    const bySize = new Map();
    for (const v of variants) {
      const sizeValue = v.size || (v.options && sizeOptionIndex >= 0 ? v.options[sizeOptionIndex] : "") || "";
      if (!sizeValue) continue;
      const existing = bySize.get(sizeValue);
      if (!existing) {
        bySize.set(sizeValue, v);
      } else {
        const vIsSelected = v.id?.toString() === selectedVariantId?.toString();
        const existingIsSelected = existing.id?.toString() === selectedVariantId?.toString();
        if (vIsSelected && !existingIsSelected) bySize.set(sizeValue, v);
        else if (!vIsSelected && existingIsSelected) { /* garder existing */ }
        else if (v.available !== false && existing.available === false) bySize.set(sizeValue, v);
        /* sinon garder existing */
      }
    }
    return Array.from(bySize.values());
  }

  /**
   * Remplit le drawer avec les données du produit
   * @param {Object} productData - Données du produit
   */
  async populate(productData) {
    this.debug('populate() start', {
      productId: productData?.productId,
      productHandle: productData?.productHandle,
      selectedVariantId: productData?.selectedVariantId,
      price: productData?.price,
      compareAtPrice: productData?.compareAtPrice,
    });
    // 1. Titre
    if (this.titleElement) {
      this.titleElement.textContent = productData.title || "";
    }

    // 2. Couleur - récupérer depuis productData ou depuis le DOM
    if (this.colorElement) {
      this.colorElement.innerHTML = "";

      let colorText = productData.color || null;

      // Si pas de couleur dans productData, chercher dans le DOM
      if (!colorText) {
        const productCards = document.querySelectorAll('.product-card, component-product-card, .collection__product-card');
        let switchColorSwatch = null;

        for (const productCard of productCards) {
          switchColorSwatch = productCard.querySelector('.product-switch-color .color-swatch.selected, switch-color-component .color-swatch.selected');
          if (switchColorSwatch) break;
        }

        if (!switchColorSwatch) {
          const switchColorComponents = document.querySelectorAll('switch-color-component');
          for (const component of switchColorComponents) {
            switchColorSwatch = component.querySelector('.color-swatch.selected');
            if (switchColorSwatch) break;
          }
        }

        if (switchColorSwatch) {
          colorText = switchColorSwatch.getAttribute('data-color-name') ||
                      switchColorSwatch.getAttribute('title')?.split(' - ')[0]?.trim() ||
                      switchColorSwatch.getAttribute('aria-label')?.split(' - ')[0]?.trim();
        }
      }

      // Afficher la couleur
      if (colorText) {
        const colorSpan = document.createElement("span");
        colorSpan.className = "quick-add-drawer-color-text";
        colorSpan.textContent = colorText;
        this.colorElement.appendChild(colorSpan);
      }
    }

    // 3. Prix
    if (this.priceElement) {
      this.priceElement.innerHTML = "";

      const price = productData.price;
      const compareAtPrice = productData.compareAtPrice;

      if (Number.isFinite(price)) {
        const priceContainer = document.createElement("div");
        priceContainer.className = "price__container";

        const priceDiv = document.createElement("div");
        priceDiv.className = (Number.isFinite(compareAtPrice) && compareAtPrice > price) ? "price__sale" : "price__regular";

        const priceSpan = document.createElement("span");
        priceSpan.className = "jc-text-sm";
        priceSpan.textContent = this.formatMoney(price);
        priceDiv.appendChild(priceSpan);

        if (Number.isFinite(compareAtPrice) && compareAtPrice > price) {
          const compareSpan = document.createElement("s");
          compareSpan.className = "!jc-mr-[.8rem] jc-text-sm";
          compareSpan.textContent = this.formatMoney(compareAtPrice);
          priceDiv.appendChild(compareSpan);
        }

        priceContainer.appendChild(priceDiv);
        this.priceElement.appendChild(priceContainer);
      }
    }

    // 4. Tailles
    if (this.sizesContainer) {
      this.sizesContainer.innerHTML = '<div class="tr-text-sm tr-text-gray-500">Chargement...</div>';

      let variants = productData.variants || [];
      // Si pas de variants et qu'on a un productHandle, récupérer depuis l'API
      if (variants.length === 0 && productData.productHandle) {
        try {
          const response = await fetch(`/products/${productData.productHandle}.js`);
          if (response.ok) {
            const product = await response.json();
            variants = product.variants || [];

            // Filtrer par couleur si une couleur est sélectionnée
            if (productData.color && variants.length > 0) {
              // Trouver l'index de l'option couleur
              let colorOptionIndex = -1;
              if (product.options && Array.isArray(product.options)) {
                colorOptionIndex = product.options.findIndex((option) => {
                  let optionName = '';
                  if (typeof option === 'string') {
                    optionName = option.toLowerCase();
                  } else if (option && typeof option === 'object' && option.name) {
                    optionName = String(option.name).toLowerCase();
                  } else if (option) {
                    optionName = String(option).toLowerCase();
                  }
                  return optionName && (optionName.includes("couleur") || optionName.includes("color"));
                });
              }

              if (colorOptionIndex >= 0) {
                const beforeFilter = variants.length;
                variants = variants.filter((v) => v.options && v.options[colorOptionIndex] === productData.color);
              }
            }
          }
        } catch (error) {
          // Erreur silencieuse
        }
      }

      // Si le prix n'a pas été fourni (ou est resté vide), le déduire du variant sélectionné
      // (utile quand getProductData n'a pas pu lire le DOM / quand le contenu est injecté mais sans texte)
      if (this.priceElement && !this.priceElement.textContent?.trim()) {
        const selected = variants.find((v) => v?.id?.toString() === productData.selectedVariantId?.toString()) || variants[0];
        const derivedPrice = Number(selected?.price);
        // Shopify product.js expose compare_at_price
        const derivedCompareAt = Number(selected?.compare_at_price ?? selected?.compareAtPrice);

        this.debug('price derived from variants', {
          selectedVariantId: productData.selectedVariantId,
          selectedVariantFound: !!selected,
          derivedPrice,
          derivedCompareAt,
          derivedPriceType: typeof selected?.price,
          derivedCompareAtType: typeof (selected?.compare_at_price ?? selected?.compareAtPrice),
        });

        if (Number.isFinite(derivedPrice) && derivedPrice > 0) {
          this.priceElement.innerHTML = "";
          const priceContainer = document.createElement("div");
          priceContainer.className = "price__container";

          const priceDiv = document.createElement("div");
          priceDiv.className =
            (Number.isFinite(derivedCompareAt) && derivedCompareAt > derivedPrice) ? "price__sale" : "price__regular";

          const priceSpan = document.createElement("span");
          priceSpan.className = "jc-text-sm";
          priceSpan.textContent = this.formatMoney(derivedPrice);
          priceDiv.appendChild(priceSpan);

          if (Number.isFinite(derivedCompareAt) && derivedCompareAt > derivedPrice) {
            const compareSpan = document.createElement("s");
            compareSpan.className = "!jc-mr-[.8rem] jc-text-sm";
            compareSpan.textContent = this.formatMoney(derivedCompareAt);
            priceDiv.appendChild(compareSpan);
          }

          priceContainer.appendChild(priceDiv);
          this.priceElement.appendChild(priceContainer);
        }
      }

      this.sizesContainer.innerHTML = "";

      if (variants.length === 0) {
        this.sizesContainer.innerHTML = '<div class="tr-text-sm tr-text-gray-500">Aucune taille disponible</div>';
        return;
      }

      // Trouver l'index de l'option taille
      const sizeOptionIndex = productData.sizeOptionIndex >= 0
        ? productData.sizeOptionIndex
        : (variants[0]?.options?.length > 0 ? variants[0].options.length - 1 : -1);

      // Une seule entrée par taille (évite doublons quand produit a couleur + taille)
      const variantsBySize = this._deduplicateVariantsBySize(variants, sizeOptionIndex, productData.selectedVariantId);

      // Créer les boutons de taille
      variantsBySize.forEach((variant) => {
        const sizeValue = variant.size || (variant.options && sizeOptionIndex >= 0 ? variant.options[sizeOptionIndex] : "") || "";
        if (!sizeValue) {
          return;
        }

        const button = document.createElement("button");
        button.type = "button";
        button.className = "quick-add-drawer-size-button";
        button.textContent = sizeValue;
        button.dataset.variantId = variant.id;
        button.dataset.sizeValue = sizeValue;

        if (variant.available === false) {
          button.disabled = true;
        }

        if (variant.id.toString() === productData.selectedVariantId?.toString()) {
          button.classList.add("selected");
          this.selectedVariantId = variant.id;
          if (this.variantIdInput) {
            this.variantIdInput.value = variant.id;
          }
        }

        button.addEventListener("click", this.handleSizeClick);
        this.sizesContainer.appendChild(button);
      });

    } else {
    }

    // Guide des tailles
    if (this.sizeGuideContainer) {
      this.sizeGuideContainer.innerHTML = "";
      if (productData.sizeGuideImage) {
        const guideLink = document.createElement("button");
        guideLink.type = "button";
        guideLink.className = "quick-add-drawer-size-guide-link";
        guideLink.addEventListener("click", this.handleSizeGuideClick);

        const guideText = document.createElement("span");
        guideText.className = "size-guide-text";
        guideText.textContent = "Guide des tailles";

        guideLink.appendChild(guideText);
        this.sizeGuideContainer.appendChild(guideLink);

        // Stocker l'image du guide pour l'ouvrir dans le modal
        this.sizeGuideImage = productData.sizeGuideImage;
        this.sizeGuideTitle = productData.title;
      }
    }

    this.debug('populate() end', {
      priceHtml: this.priceElement?.innerHTML,
      priceText: this.priceElement?.textContent,
    });
  }

  /**
   * Gère le clic sur une taille - ajoute directement au panier
   */
  async handleSizeClick(event) {
    const button = event.currentTarget;
    if (button.disabled) return;

    // Retirer la sélection précédente
    this.sizesContainer.querySelectorAll(".quick-add-drawer-size-button").forEach((btn) => {
      btn.classList.remove("selected");
    });

    // Ajouter la sélection
    button.classList.add("selected");
    this.selectedVariantId = button.dataset.variantId;

    // Mettre à jour l'input hidden
    if (this.variantIdInput) {
      this.variantIdInput.value = this.selectedVariantId;
    }

    // Ajouter directement au panier
    await this.handleSubmit(event);
  }

  /**
   * Gère le clic sur le guide des tailles
   */
  handleSizeGuideClick(event) {
    event.preventDefault();
    if (!this.sizeGuideImage) return;

    // Utiliser le modal du drawer ou celui global
    const background = this.querySelector("#quick-add-drawer-size-guide-background") || document.getElementById("sizes-grid-background");
    const sizesGrid = this.querySelector("#quick-add-drawer-size-guide") || document.getElementById("sizes-grid");

    if (background && sizesGrid) {
      // Mettre à jour l'image et le titre
      const img = sizesGrid.querySelector(".quick-add-drawer-size-guide-image") || sizesGrid.querySelector("img");
      const title = sizesGrid.querySelector(".quick-add-drawer-size-guide-title") || sizesGrid.querySelector("h3");

      if (img) {
        img.src = this.sizeGuideImage;
      }
      if (title && this.sizeGuideTitle) {
        title.textContent = this.sizeGuideTitle;
      }

      // Afficher le modal
      background.classList.remove("!jc-hidden", "jc-opacity-0");
      background.classList.add("jc-opacity-100");
      sizesGrid.classList.remove("jc-translate-y-[-300px]");
      sizesGrid.classList.add("jc-translate-y-0");

      // Gérer la fermeture
      const closeHandler = () => {
        background.classList.add("!jc-hidden", "jc-opacity-0");
        background.classList.remove("jc-opacity-100");
        sizesGrid.classList.add("jc-translate-y-[-300px]");
        sizesGrid.classList.remove("jc-translate-y-0");
        // Nettoyer les event listeners
        exitButton?.removeEventListener("click", closeHandler);
        background.removeEventListener("click", backgroundCloseHandler, true);
        sizesGrid.removeEventListener("click", preventPropagation);
      };

      const backgroundCloseHandler = (e) => {
        const content = document.getElementById("quick-add-drawer-size-guide-content") || document.getElementById("sizes-grid-content") || sizesGrid.firstElementChild;
        if (!content || !content.contains(e.target)) {
          closeHandler();
        }
      };

      // Empêcher la propagation du clic sur le contenu du modal
      const preventPropagation = (e) => {
        e.stopPropagation();
      };

      const exitButton = sizesGrid.querySelector("#quick-add-drawer-size-guide-exit") || sizesGrid.querySelector("#sizes-grid-exit");
      if (exitButton) {
        exitButton.addEventListener("click", closeHandler);
      }

      // Empêcher la propagation du clic sur le contenu du modal
      sizesGrid.addEventListener("click", preventPropagation);

      // Écouter en phase capture pour recevoir le clic même si sizesGrid stoppe la propagation
      background.addEventListener("click", backgroundCloseHandler, true);
    }
  }

  /**
   * Gère le clic sur l'overlay
   */
  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }

  /**
   * Gère le clic sur le bouton fermer
   */
  handleClose(event) {
    event.preventDefault();
    this.close();
  }

  /**
   * Gère la touche Escape
   */
  handleEscape(event) {
    if (event.key === "Escape" && this.classList.contains("is-open")) {
      this.close();
    }
  }

  /**
   * Gère la soumission (ajout au panier)
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (!this.selectedVariantId) {
      // Si aucune taille sélectionnée, utiliser la première disponible
      const firstAvailable = this.sizesContainer?.querySelector(".quick-add-drawer-size-button:not(:disabled)");
      if (firstAvailable) {
        this.selectedVariantId = firstAvailable.dataset.variantId;
      } else {
        return;
      }
    }

    if (!this.variantIdInput) {
      return;
    }

    this.variantIdInput.value = this.selectedVariantId;

    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add("loading");
    }

    const formData = new FormData(this.form);

    // Ajouter les sections pour le cart drawer
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && cartDrawer.getSectionsToRender) {
      formData.append(
        "sections",
        cartDrawer.getSectionsToRender().map((s) => s.id)
      );
      formData.append("sections_url", window.location.pathname);
      cartDrawer.setActiveElement(document.activeElement);
    }

    try {
      // Utiliser fetchConfig comme dans quick-add.js
      const fetchConfig = (type = 'json', config = {}) => {
        const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };
        if (type === 'javascript') {
          headers['X-Requested-With'] = 'XMLHttpRequest';
          delete headers['Content-Type'];
        }
        return {
          method: 'POST',
          headers: headers,
          body: config.body,
        };
      };

      const cartAddUrl = window.routes?.cart_add_url || window.Theme?.routes?.cart_add_url || "/cart/add.js";

      // Utiliser le même format que quick-add.js
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(cartAddUrl, config);

      // Vérifier que la réponse est OK avant de parser
      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const data = await response.json();

      if (response.ok && !data.status) {
        // Fermer le drawer
        this.close();

        // Ouvrir le cart drawer
        if (cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents(data);
          cartDrawer.classList.remove("is-empty");
          if (cartDrawer.open) {
            cartDrawer.open();
          }
        }
      } else {
        alert("Erreur lors de l'ajout au panier. Veuillez réessayer.");
      }
    } catch (error) {
      alert("Erreur lors de l'ajout au panier. Veuillez réessayer.");
    } finally {
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove("loading");
      }
    }
  }

  /**
   * Formate un prix en centimes vers le format monétaire
   */
  formatMoney(cents) {
    if (cents == null || cents === 0) return "";
    // Utiliser le format Shopify si disponible avec le format du thème
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || "{{amount_with_comma_separator}} €");
    }
    // Fallback : format simple avec décimales
    const amount = (cents / 100).toFixed(2);
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}

if (!customElements.get("quick-add-drawer")) {
  customElements.define("quick-add-drawer", QuickAddDrawer);
} else {
}
